import { Layers, ToggleLeft } from 'lucide-react';
import React, { useState } from 'react';

const Toggle = ({
    label,
    description,
    icon,
    defaultChecked = false,
    onChange,
    variant = "default", // "default", "filter", "darkmode"
    filterType = "none", // "blur", "brightness", "grayscale"
    isDarkMode = false
}) => {
    const [isChecked, setIsChecked] = useState(defaultChecked);

    const handleToggle = () => {
        const newValue = !isChecked;
        setIsChecked(newValue);
        onChange?.(newValue);
    };

    const getFilterEffect = () => {
        if (!isChecked || variant !== "filter") return "";

        switch (filterType) {
            case "blur":
                return "blur-sm";
            case "brightness":
                return "brightness-150 contrast-110";
            case "grayscale":
                return "grayscale";
            default:
                return "";
        }
    };

    return (
        <>
        <div className={`p-6 rounded-lg transition-all duration-300 hover:shadow-xl bg-color-white/80 border border-color-gray-200 shadow-sm dark:bg-color-gray-800 dark:border-color-gray-700`}>
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                    {icon && (
                        <div className={`w-10 rounded-lg overflow-hidden transition-all duration-500 ${getFilterEffect()}`}>
                            {icon}
                        </div>
                    )}
                    <div>
                        <h3 className={`font-semibold text-lg`}>
                            {label}
                        </h3>
                        {description && (
                            <p className={`text-sm mt-1 text-color-gray-600 dark:text-color-gray-400`}>
                                {description}
                            </p>
                        )}
                    </div>
                </div>

                <button
                    onClick={handleToggle}
                    className={`relative w-14 h-8 rounded-full transition-all duration-300 ease-in-out ${isChecked ? 'bg-color-blue-500' : 'bg-color-gray-200'}
                            }`}
                >
                    <div
                        className={`absolute top-1 left-1 w-6 h-6 rounded-full transition-all duration-300 ease-in-out transform bg-color-white shadow-md ${isChecked ? 'translate-x-6' : 'translate-x-0'
                            } `}
                    />
                </button>
            </div>
        </div>
        <div className='sr-only brightness-150 contrast-110'></div>
        </>
    );
};

// Demo Component
const ToggleDemo = () => {
    const [filters, setFilters] = useState({
        blur: false,
        brightness: false,
        grayscale: false
    });

    const [darkMode, setDarkMode] = useState(false);

    const handleFilterChange = (filterName) => (value) => {
        setFilters(prev => ({ ...prev, [filterName]: value }));
    };


    return (
        <div className={`col-span-2`}>
            <div className="grid grid-columns-1 xl:grid-columns-2 gap-8">
                {/* Filters Section */}
                <div className='bg-color-white rounded-2xl p-8 shadow-xs border border-color-gray-200 dark:bg-color-gray-900 dark:border-color-gray-700'>
                    <div className="flex mb-6">
                        <div className="w-12 h-12 shrink-0 bg-color-gray-100 rounded-lg flex items-center justify-center mr-4 dark:bg-color-gray-700">
                            <Layers className="w-6 h-6 text-color-gray-600 dark:text-color-gray-200" />
                        </div>
                        <div>
                            <h3 className="text-xl font-semibold">Filters</h3>
                            <p className="text-color-gray-600 text-sm dark:text-color-gray-400">What’s a website these days without a few backdrop blurs? Keep stacking filters until your designer asks you to please, please stop.</p>
                        </div>
                    </div>
                    <div className="space-y-4">
                        <Toggle
                            label="Blur Effect"
                            description="Add a subtle blur effect to your images"
                            icon={<img alt="img" src='https://fastly.picsum.photos/id/866/600/600.jpg?hmac=dKQ662qAsYvwCo90SUl-0iG9K0Yp3ySEK0wJVIYh5uo'/>}
                            variant="filter"
                            filterType="blur"
                            
                            defaultChecked={filters.blur}
                            onChange={handleFilterChange('blur')}
                        />

                        <Toggle
                            label="Brightness Boost"
                            description="Increase the brightness of your photos"
                            icon={<img alt="img" src='https://fastly.picsum.photos/id/866/600/600.jpg?hmac=dKQ662qAsYvwCo90SUl-0iG9K0Yp3ySEK0wJVIYh5uo'/>}
                            variant="filter"
                            filterType="brightness"
                            
                            defaultChecked={filters.brightness}
                            onChange={handleFilterChange('brightness')}
                        />

                        <Toggle
                            label="Grayscale Mode"
                            description="Convert images to black and white"
                            icon={<img alt="img" src='https://fastly.picsum.photos/id/866/600/600.jpg?hmac=dKQ662qAsYvwCo90SUl-0iG9K0Yp3ySEK0wJVIYh5uo'/>}
                            variant="filter"
                            filterType="grayscale"
                            
                            defaultChecked={filters.grayscale}
                            onChange={handleFilterChange('grayscale')}
                        />
                    </div>
                </div>

                {/* Settings Section */}
                <div className='bg-color-white rounded-2xl p-8 shadow-xs border border-color-gray-200 dark:bg-color-gray-900 dark:border-color-gray-700'>
                    <div className="flex mb-6">
                        <div className="w-12 h-12 shrink-0 bg-color-gray-100 rounded-lg flex items-center justify-center mr-4 dark:bg-color-gray-700">
                            <ToggleLeft className="w-6 h-6 text-color-gray-600 dark:text-color-gray-200" />
                        </div>
                        <div>
                            <h3 className="text-xl font-semibold">Dark mode</h3>
                            <p className="text-color-gray-600 text-sm dark:text-color-gray-400">If you’re not a fan of burning your retinas, just stick dark: in front of any color to apply it in dark mode.</p>
                        </div>
                    </div>
                    <div className="space-y-4">
                        <Toggle
                            label="Dark Mode"
                            description="Switch to dark theme for better night viewing"
                            variant="darkmode"
                            onChange={setDarkMode}
                        />
                        <div className={`rounded-lg mt-10 bg-color-white px-6 py-8 shadow-lg ring ring-color-gray-900/5 ${darkMode ? 'bg-color-gray-800 ring-color-gray-700' : ''}`}>
                            <div>
                                <span className={`inline-flex items-center justify-center rounded-md bg-color-indigo-500 p-2 shadow-lg ${darkMode ? 'shadow-none' : ''}`}>
                                    <svg className="h-6 w-6 text-color-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                                    </svg>
                                </span>
                            </div>
                            <p class={`mt-5 text-base font-medium tracking-tight text-color-gray-900 ${darkMode ? 'text-color-gray-100' : ''}`}>Writes upside-down</p>
                            <p class={`mt-2 text-sm text-color-gray-500 ${darkMode ? 'text-color-gray-400' : ''}`}>The Zero Gravity Pen can be used to write in any orientation,
                                including upside-down. It even works in outer space.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ToggleDemo;